import { Form, useField, useFormikContext } from "formik";
import React, { ComponentProps, useContext, HTMLProps } from "react";
import styled from "styled-components";
import { ButtonProps, FormSelect } from "react-bootstrap";

import { MainButton } from "./button";

const FieldDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

type FieldProps = {
  name: string;
  className?: string;
  children: React.ReactNode;
};

const FieldContext = React.createContext({ name: "" });
export const useFieldName = () => useContext(FieldContext).name;

export const Field = ({ name, className, children }: FieldProps) => {
  const [_, meta] = useField({ name });
  return (
    <FieldDiv className={`${className ?? ""} my-3`}>
      <FieldContext.Provider value={{ name }}>{children}</FieldContext.Provider>
      {meta.touched && meta.error ? (
        <div className="error text-danger small">{meta.error}</div>
      ) : null}
    </FieldDiv>
  );
};

export const Label = ({ ...props }: HTMLProps<HTMLLabelElement>) => {
  const name = useFieldName();

  return (
    <>
      <label className="small fw-bold mb-1" htmlFor={name} {...props} />
    </>
  );
};

export const Input = ({ ...props }: HTMLProps<HTMLInputElement>) => {
  const name = useFieldName();
  const [field] = useField(name);
  return (
    <input
      className="form-control"
      {...field}
      {...props}
      id={field.name}
      value={field.value}
    />
  );
};

export const Select = ({ ...props }: ComponentProps<typeof FormSelect>) => {
  const name = useFieldName();
  const [field] = useField(name);
  return <FormSelect {...field} id={field.name} {...props} />;
};

export const SelectOption = (props: HTMLProps<HTMLOptionElement>) => (
  <option {...props} />
);

export const Checkbox = ({ ...props }: HTMLProps<HTMLInputElement>) => {
  const name = useFieldName();
  const [field, _, { setValue }] = useField(name);
  return (
    <input
      type="checkbox"
      className="form-check-input"
      checked={field.value}
      id={field.name}
      name={field.name}
      onChange={(e) => setValue(e.target.checked)}
      {...props}
    />
  );
};

export const CheckboxWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => <div className="form-check">{children}</div>;

export const CheckboxLabel = (props: HTMLProps<HTMLLabelElement>) => (
  <Label className="form-check-label" {...props} />
);

export const SubmitButton = (props: ButtonProps) => {
  const { isSubmitting } = useFormikContext();
  return (
    <MainButton
      type="submit"
      {...props}
      disabled={props.disabled}
      isSubmitting={isSubmitting}
    />
  );
};

export { Form };
